/**
 * Essay Player
 * Split-view player: lesson content on left, essay writing area on right
 * Responsive: stacks vertically on small screens
 */

import * as React from 'react';
import {
    cn,
    Card,
    RichTextEditor,
    RichTextViewer,
    ScoreGradeComponent,
    Alert,
    AlertDescription,
    FeedbackCard,
    useIsMobile,
    tiptapJsonToPlainText,
    createParagraphContent,
    isTiptapDocument,
} from '@ai-content-hub/content-kit';
import type { ScoreGradeResult, TiptapJSON } from '@ai-content-hub/content-kit';
import { isCorrectAnswerRevealed, useLocale, ContentPlayerSplitLayout } from '@ai-content-hub/content-core';
import { PagePlayer, hasVisiblePageContent } from '@ai-content-hub/content-elements';
import type { EssayData, EssayConfig, EssayLayoutConfig } from './schema';
import { defaultEssayData } from './schema';
import { BookOpen, PenLine } from 'lucide-react';

function isScoreGradeResult(details: unknown): details is ScoreGradeResult {
    if (!details || typeof details !== 'object') return false;
    const d = details as Record<string, unknown>;
    return typeof d.overall_score_percentage === 'number';
}

function countWords(text: string): number {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
}

export interface EssayPlayerProps {
    data: EssayData;
    config?: EssayConfig;
    answer?: { text?: string; richText?: TiptapJSON; wordCount?: number } | null;
    onAnswer?: (answer: { text: string; richText: TiptapJSON; wordCount: number }) => void;
    showFeedback?: boolean;
    isReview?: boolean;
    disabled?: boolean;
    className?: string;
    result?: { details?: Record<string, unknown> } & Record<string, unknown>;
}

export const EssayPlayer: React.FC<EssayPlayerProps> = ({
    data,
    config,
    answer: answerProp,
    onAnswer,
    showFeedback,
    isReview = false,
    disabled = false,
    className,
    result: resultProp,
}) => {
    const layoutConfig = config?.layout as EssayLayoutConfig | undefined;
    const i18nConfig = config?.i18n;
    const { t } = useLocale({ config: i18nConfig });
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false.
    const revealCorrect = isCorrectAnswerRevealed(config);

    const splitRatio = layoutConfig?.splitRatio ?? '50-50';
    const minHeight = layoutConfig?.minHeight ?? '500px';
    const responsive = layoutConfig?.responsive !== false;
    const panelLayoutType = layoutConfig?.type ?? 'auto';
    const isAutoPanelLayout = panelLayoutType === 'auto';

    const isMobile = useIsMobile(768);
    const showSplitLayout = isAutoPanelLayout ? !isMobile : !responsive || !isMobile;

    const safeData: EssayData = {
        ...defaultEssayData,
        ...data,
        lessonContent: data?.lessonContent ?? defaultEssayData.lessonContent,
        essayQuestion: data?.essayQuestion ?? defaultEssayData.essayQuestion,
    };
    const submittedOrFeedback = Boolean(showFeedback || isReview);

    const [richText, setRichText] = React.useState<TiptapJSON>(() => {
        const rt = (answerProp as { richText?: TiptapJSON } | null)?.richText;
        if (rt && typeof rt === 'object' && rt.type === 'doc') return rt;
        const txt = (answerProp as { text?: string } | null)?.text;
        return typeof txt === 'string' && txt.trim()
            ? createParagraphContent(txt)
            : { type: 'doc', content: [{ type: 'paragraph' }] };
    });

    React.useEffect(() => {
        if (!submittedOrFeedback) return;
        const rt = (answerProp as { richText?: TiptapJSON } | null)?.richText;
        if (rt && typeof rt === 'object' && rt.type === 'doc') {
            setRichText(rt);
            return;
        }
        const txt = (answerProp as { text?: string } | null)?.text;
        if (typeof txt === 'string' && txt.trim()) {
            setRichText(createParagraphContent(txt));
        }
    }, [answerProp, submittedOrFeedback]);

    const plainText = React.useMemo(() => tiptapJsonToPlainText(richText), [richText]);
    const wordCount = countWords(plainText);
    const minWords = safeData.essayQuestion.minWords ?? 250;
    const maxWords = safeData.essayQuestion.maxWords ?? 500;
    const isWithinRange = wordCount >= minWords && wordCount <= maxWords;

    const handleRichTextChange = React.useCallback(
        (doc: TiptapJSON) => {
            setRichText(doc);
            const text = tiptapJsonToPlainText(doc);
            onAnswer?.({ text, richText: doc, wordCount: countWords(text) });
        },
        [onAnswer]
    );

    if (!data) {
        return (
            <div
                className={cn(
                    'essay-player essay-player--empty text-muted-foreground italic p-4',
                    className
                )}
            >
                {t('noQuestions', 'No content available')}
            </div>
        );
    }

    // EAA-2063: a lesson page holding only hidden blocks (rubric hints for the grader) is empty.
    const hasLessonContent = hasVisiblePageContent(safeData.lessonContent);
    const hasRootDescription = Boolean(safeData.description?.trim());

    const instructionsHeader = hasRootDescription ? (
        <div className="essay-player-header essay-player-header--intro flex-shrink-0 space-y-2">
            <p className="essay-player-description text-sm text-muted-foreground leading-relaxed">
                {safeData.description}
            </p>
        </div>
    ) : null;

    const renderWritingArea = () => (
        <div className="essay-player-writing-area flex flex-col min-h-0 min-w-0 flex-1 gap-4">
            {safeData.essayQuestion.question &&
                (() => {
                    const q = safeData.essayQuestion.question;
                    if (q && typeof q === 'object' && isTiptapDocument(q)) {
                        return <RichTextViewer data={q} className="max-w-full!" />;
                    }
                    if (typeof q === 'string' && q.trim()) {
                        return <p className="text-xl leading-relaxed min-w-0 break-words">{q}</p>;
                    }
                    return null;
                })()}

            <div className="flex-1 flex flex-col min-h-0">
                <RichTextEditor
                    value={richText}
                    onChange={handleRichTextChange}
                    placeholder={t('essayPlaceholder', 'Write your essay here...')}
                    readOnly={disabled || submittedOrFeedback}
                    minHeight={200}
                    className="flex-1"
                    features={[
                        'bold',
                        'italic',
                        'underline',
                        'heading',
                        'bulletList',
                        'orderedList',
                        'align',
                    ]}
                />
            </div>

            <div className="flex-shrink-0 flex flex-wrap items-center gap-3 text-sm min-w-0">
                <span
                    className={cn(
                        'font-medium',
                        wordCount < minWords && 'text-destructive',
                        wordCount > maxWords && 'text-destructive',
                        isWithinRange && 'text-success'
                    )}
                >
                    {wordCount}{' '}
                    {wordCount === 1 ? t('wordLabel', 'word') : t('wordsLabel', 'words')}
                </span>
                <span className="text-muted-foreground">
                    ({t('minWords', 'Min')}: {minWords} / {t('maxWords', 'Max')}: {maxWords})
                </span>
            </div>

            {/* EAA-1873: Writing is a non-media interaction, so its authored or
            generated rationale belongs in post-submit or review feedback. */}
            {submittedOrFeedback && revealCorrect && safeData.essayQuestion.explanation?.trim() ? (
                <div className="essay-player-explanation flex-shrink-0 min-w-0 break-words">
                    <FeedbackCard type="explanation" title={t('explanation', 'Explanation')}>
                        {safeData.essayQuestion.explanation}
                    </FeedbackCard>
                </div>
            ) : null}

            {showFeedback && (
                <div className="flex-shrink-0 space-y-4 min-w-0 break-words">
                    {/* ELMS-2370: hide answer => no AI score card (neutral "submitted" notice only). */}
                    {revealCorrect && resultProp?.details && isScoreGradeResult(resultProp.details) ? (
                        <ScoreGradeComponent
                            result={resultProp.details}
                            className="essay-score-grade"
                            translate={t}
                        />
                    ) : (
                        <Alert className="bg-success/10 border-success/30 text-success">
                            <AlertDescription className="text-success/90">
                                {t(
                                    'submittedSuccessfully',
                                    'Your essay has been submitted successfully.'
                                )}
                            </AlertDescription>
                        </Alert>
                    )}
                </div>
            )}
        </div>
    );

    if (!showSplitLayout || panelLayoutType === 'vertical') {
        return (
            <div className={cn('essay-player flex flex-col', className)} style={{ minHeight }}>
                {instructionsHeader}
                <div className="flex-1 flex flex-col gap-6 overflow-y-auto">
                    {hasLessonContent && (
                        <div className="essay-player-content-header flex-shrink-0">
                            <div className="flex items-center gap-2 pb-3 border-b mb-3">
                                <BookOpen className="h-5 w-5 text-primary" />
                                <h2 className="font-semibold text-lg">
                                    {t('contentTab', 'Content')}
                                </h2>
                            </div>
                            <Card>
                                <div className="p-4">
                                    <PagePlayer
                                        data={safeData.lessonContent}
                                        submitted={submittedOrFeedback && revealCorrect}
                                    />
                                </div>
                            </Card>
                        </div>
                    )}

                    <div className="essay-player-essay-header flex-shrink-0">
                        <div className="flex items-center gap-2 pb-3 border-b mb-3">
                            <PenLine className="h-5 w-5 text-primary" />
                            <h2 className="font-semibold text-lg">{t('essayTab', 'Essay')}</h2>
                        </div>
                        {renderWritingArea()}
                    </div>
                </div>
            </div>
        );
    }

    if (!hasLessonContent) {
        return (
            <div
                className={cn('essay-player flex flex-col flex-1 min-h-0', className)}
                style={{ minHeight }}
            >
                {instructionsHeader}
                <div className="essay-player-essay-panel flex flex-col min-h-0 min-w-0 overflow-hidden flex-1">
                    <div className="essay-player-essay-header flex-shrink-0 flex items-center gap-2 pb-3 border-b mb-3 min-w-0">
                        <PenLine className="h-5 w-5 shrink-0 text-primary" />
                        <h2 className="font-semibold text-lg truncate">{t('essayTab', 'Essay')}</h2>
                    </div>
                    <div className="flex-1 min-h-0 min-w-0 overflow-auto break-words">
                        {renderWritingArea()}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn('essay-player flex flex-col flex-1 min-h-0', className)}
            style={{ minHeight }}
        >
            {instructionsHeader}
            <ContentPlayerSplitLayout
                prefix="essay-player-split"
                splitRatio={splitRatio}
                leftPanel={
                    <div className="essay-player-content-panel flex flex-col min-h-0 min-w-0 overflow-hidden">
                        <div className="essay-player-content-header flex-shrink-0 flex items-center gap-2 pb-3 border-b mb-3">
                            <BookOpen className="h-5 w-5 text-primary" />
                            <h2 className="font-semibold text-lg">{t('contentTab', 'Content')}</h2>
                        </div>
                        <Card className="flex-1 min-h-0 min-w-0 overflow-auto border-none">
                            <div className="min-h-0 min-w-0 overflow-auto p-1 break-words">
                                <PagePlayer
                                    data={safeData.lessonContent}
                                    submitted={submittedOrFeedback && revealCorrect}
                                />
                            </div>
                        </Card>
                    </div>
                }
                rightPanel={
                    <div className="essay-player-essay-panel flex flex-col min-h-0 min-w-0 overflow-hidden">
                        <div className="essay-player-essay-header flex-shrink-0 flex items-center gap-2 pb-3 border-b mb-3 min-w-0">
                            <PenLine className="h-5 w-5 shrink-0 text-primary" />
                            <h2 className="font-semibold text-lg truncate">
                                {t('essayTab', 'Essay')}
                            </h2>
                        </div>
                        <div className="flex-1 min-h-0 min-w-0 overflow-auto break-words">
                            {renderWritingArea()}
                        </div>
                    </div>
                }
            />
        </div>
    );
};

export default EssayPlayer;
